<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onBeforeMount,
  onMounted,
  nextTick,
  defineAsyncComponent,
} from "vue";
import LoaderInline from "@/components/LoaderInline.vue";
import ButtonIcon from "@/components/common/ButtonIcon.vue";
import SectionUpdateMessageModal from "@/components/layouts/SectionUpdateMessageModal.vue";
import MainSidebar from "@/modules/sidebar/MainSidebar.vue";
import MainNotify from "@/modules/main-notify/components/MainNotify.vue";
import UiButton from "@/ui/UiButton.vue";
import AndroidPwaModal from "@/modules/pwa/components/AndroidPwaModal.vue";
import NotShowAnymoreModal from "@/modules/pwa/components/NotShowAnymoreModal.vue";
import { usePlatform } from "@/modules/pwa/composables/use-platform";
import IosPwaModal from "@/modules/pwa/components/IosPwaModal.vue";
import SwitchLanguageModal from "@/modules/language/components/SwitchLanguageModal.vue";
import { useMainNotifyStore } from "@/modules/main-notify/store";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useWindowSize } from "@vueuse/core";
import { Notify } from "@/modules/main-notify/types";
import { LegalEntity } from "@/modules/entities/types";

const Profile = defineAsyncComponent(
  () => import("@/views/cabinet/Profile.vue")
);

defineOptions({ name: "ProfileLayout" });

defineProps({
  isShowOption: {
    type: Boolean,
    default: true,
  },
});

const { isAndroid, isIOS, isPWADownload } = usePlatform();
const notifyStore = useMainNotifyStore();
const vuexStore = useStore();
const router = useRouter();
const { t } = useI18n();
const { width } = useWindowSize();

const loading = ref(false);
const permissionBalance = ref(false);
const dataForUpdateMessage = ref(null);
const isModalOpen = ref(false);
const notify = ref<null | Notify>(null);
const isOpenToast = ref(false);
const oldSubtypes = ref([
  "tt-update",
  "clients-update",
  "product-release",
  "review-update-release",
]);

const isOpenAndroidPwaModal = ref(false);
const isOpenIosPwaModal = ref(false);
const isOpenNotShowPwaModal = ref(false);

const entities = computed<LegalEntity[]>(() => vuexStore.state.entity.entities);
const profile = computed(() => vuexStore.state.user.profile);
const isShowSidebar = computed(() => vuexStore.state.main.isShowSidebar);
const isCollapsedSidebar = computed(
  () => vuexStore.state.main.isCollapsedSidebar
);
const notification = computed(() => vuexStore.state.notification);

const sideBarTransitionName = computed(() =>
  width.value < 1280 ? "slide-left" : ""
);

watch(width, (val) => {
  vuexStore.commit("main/SET_IS_SHOW_SIDEBAR", val >= 1280);
  vuexStore.commit("main/SET_IS_COLLAPSED_SIDEBAR", false);
});

watch(notification, (newVal) => {
  isOpenToast.value = !!newVal;
});

const checkOpenDownloadPwaModal = () => {
  const isPwaModal = localStorage.getItem("isPwaModal");
  if (isPwaModal === "off") {
    return;
  }

  if (!isPWADownload.value && isAndroid.value) {
    isOpenAndroidPwaModal.value = true;
  } else if (!isPWADownload.value && isIOS.value) {
    isOpenIosPwaModal.value = true;
  }
};

const logoutAll = async () => {
  try {
    profile.value.is_owner
      ? vuexStore.dispatch("user/logoutOwner")
      : vuexStore.dispatch("user/logout");
  } finally {
    profile.value.country_code === "kg"
      ? (window.location.href = "https://rccom.kg/")
      : router.push({ name: "Lending" });
  }

  localStorage.removeItem("creationAllowed");
};

const clickOnBalance = () => {
  router.push({ name: "Balance" });
};

const openMobileSidebar = () => {
  vuexStore.commit("main/SET_IS_SHOW_SIDEBAR", true);
  vuexStore.commit("main/SET_IS_COLLAPSED_SIDEBAR", false);
};

onBeforeMount(() => {
  localStorage.removeItem("entityRegistered");
  localStorage.removeItem("emailCode");
  localStorage.removeItem("email");
});

onMounted(async () => {
  loading.value = true;

  try {
    const response = await vuexStore.dispatch(
      "auth/internalNotificationsAccount"
    );

    const serverNotifyRes = response.data.data[0];

    if (serverNotifyRes) {
      if (!oldSubtypes.value.includes(serverNotifyRes.subtype)) {
        notify.value = {
          type: "another",
          id: serverNotifyRes.id,
          status: serverNotifyRes.additional_data.status,
          title: serverNotifyRes.header,
          text: serverNotifyRes.message,
          link: {
            to: serverNotifyRes.additional_data.url,
            label: serverNotifyRes.additional_data.label,
          },
        };

        setTimeout(() => {
          notifyStore.changeNotifyVisibility(true);
        }, 2000);
      } else {
        dataForUpdateMessage.value = serverNotifyRes;
        isModalOpen.value = true;
      }
    }

    nextTick(() => {
      vuexStore.commit("main/SET_IS_SHOW_SIDEBAR", width.value >= 1280);
    });

    const showBalanceBTN = () => {
      if (profile.value?.permissions) {
        for (const i of profile.value.permissions) {
          if (i === "balance") {
            permissionBalance.value = true;
          }
        }

        if (!profile.value.has_debt) {
          return;
        }

        setTimeout(() => {
          notifyStore.changeNotifyVisibility(true);

          notify.value = {
            id: 1,
            type: "debt",
            status: "error",
            title: t("debt.title"),
            text: t("debt.text"),
            link: {
              to: { name: "Balance" },
              label: t("debt.link"),
            },
          };
        }, 1000);
      }
    };

    const startProfileWork = async () => {
      if (!profile.value?.permissions) {
        await vuexStore.dispatch("user/getProfileInformation");
      }
      showBalanceBTN();
    };

    await Promise.all([
      startProfileWork(),

      vuexStore.dispatch("entity/getEntities").then(() => {
        if (entities.value.length === 0) {
          localStorage.setItem("emailCode", "done");
          router.push({ name: "Registration" });
        }
      }),
    ]);
  } finally {
    loading.value = false;
  }

  checkOpenDownloadPwaModal();
});
</script>

<template>
  <div class="profile-layout">
    <LoaderInline :loading="loading" />

    <div
      v-if="!loading"
      :class="{
        'layout-container_full-width': !isShowSidebar,
        'layout-container_collapsed-sidebar': isCollapsedSidebar,
      }"
      class="profile-layout__layout-container layout-container"
    >
      <Transition :name="sideBarTransitionName">
        <MainSidebar v-show="isShowSidebar" @logoutAll="logoutAll" />
      </Transition>

      <div
        ref="pageContainer"
        class="layout-container__page-container page-container"
      >
        <div class="page-container__navigation">
          <ButtonIcon
            class="page-container__burger-menu"
            @click="openMobileSidebar()"
          >
            <span class="icon-burger" />
          </ButtonIcon>

          <div class="page-container__buttons">
            <UiButton
              v-if="permissionBalance"
              class="balance"
              :label="$t('sys.balance')"
              color="grey"
              :padding="[7, 24]"
              @click="clickOnBalance"
            />
          </div>
        </div>

        <MainNotify v-if="notify" :notify="notify" />

        <RouterView />
      </div>

      <SectionUpdateMessageModal
        v-model="isModalOpen"
        :modal-data="dataForUpdateMessage"
      />
    </div>

    <AndroidPwaModal
      v-model="isOpenAndroidPwaModal"
      @openNotShowModal="isOpenNotShowPwaModal = true"
    />

    <IosPwaModal
      v-model="isOpenIosPwaModal"
      @openNotShowModal="isOpenNotShowPwaModal = true"
    />

    <NotShowAnymoreModal v-model="isOpenNotShowPwaModal" />

    <SwitchLanguageModal />

    <Profile @logout="logoutAll" />
  </div>
</template>

<style lang="scss" scoped>
.profile-layout {
  height: 100dvh;
  max-height: 100dvh;

  .layout-container {
    height: 100%;
    display: grid;
    grid-template-columns: 380px calc(100% - 380px);
    color: $new-light-primary;
    z-index: 1;
    position: relative;
    @include transition;

    &_full-width {
      grid-template-columns: 100% !important;
    }

    &_collapsed-sidebar {
      grid-template-columns: 60px calc(100% - 60px);
    }

    .page-container {
      width: 100%;
      display: grid;
      grid-template-rows: 56px calc(100% - 56px);
      height: -webkit-fill-available;
      max-height: 100vh;
      position: relative;
      z-index: 1;
      @include transition;

      @media (min-width: 768px) {
        grid-template-rows: 68px calc(100% - 68px);
      }

      &__navigation {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        padding: 16px 32px;
        border-bottom: 1px solid
          var(--color-semantic-border-normal-soft, #ebeced);
        background: var(--color-semantic-background-normal-bg-primary, #fff);
        @include transition;
      }

      .crumbs {
        display: flex;
        flex-direction: row;
        gap: 4px;

        &__block {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 4px;
        }

        &__icon {
          width: 24px;
          height: 24px;
          background: $new-light-sixth;
        }

        &_not-active {
          color: $new-light-sixth;
        }

        :not(:last-child) {
          :after {
            content: " /";
          }
        }
      }

      &__burger-menu {
        display: none;
        padding: 0;
      }

      .icon-burger {
        width: 32px;
        height: 32px;
        background: var(--color-semantic-content-normal-primary);
      }

      &__buttons {
        display: flex;
        flex-direction: row;
        gap: 16px;

        :deep(.ui-button) {
          height: 32px;

          @media (min-width: 768px) {
            height: 36px;
          }
        }

        .balance {
          @include body-bold-M;
        }

        .filter {
          display: none;
          padding: 4px;
        }

        .icon-filter {
          width: 24px;
          height: 24px;
          background: $new-light-primary;
        }
      }
    }
  }
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
  &::after {
    opacity: 0;
  }
}

@media (max-width: 1535px) {
  .profile-layout {
    .layout-container {
      grid-template-columns: 300px calc(100% - 300px);

      &_collapsed-sidebar {
        grid-template-columns: 60px calc(100% - 60px);
      }

      .page-container {
        &__navigation {
          padding: 16px 24px;
        }
      }
    }
  }
}

@media (max-width: 1535px) {
  .profile-layout {
    .page-container {
      max-height: -webkit-fill-available;
    }
  }
}

@media (max-width: 1279px) {
  .profile-layout {
    .layout-container {
      grid-template-columns: 320px calc(100% - 320px);

      &_collapsed-sidebar {
        grid-template-columns: 60px calc(100% - 60px);
      }
    }
  }
}

@media (max-width: 1279px) {
  .profile-layout {
    height: 100dvh;

    .layout-container {
      grid-template-columns: 1fr;
      position: relative;

      .page-container {
        height: 100dvh;

        &__navigation {
          justify-content: space-between;
        }

        &__burger-menu {
          line-height: 0;
          display: block;
        }
      }
    }
  }
}

@media (max-width: 767px) {
  .profile-layout {
    height: 100dvh;

    .layout-container {
      grid-template-columns: 1fr;
      position: relative;

      .page-container {
        height: 100dvh;

        &__navigation {
          justify-content: space-between;
          padding: 12px 24px;
        }

        &__burger-menu {
          display: block;
        }
      }
    }
  }
}

@media (max-width: 565px) {
  .filter {
    display: block !important;
  }
}

@media (max-width: 411px) {
  .profile-layout {
    .layout-container {
      .page-container__navigation {
        padding: 12px 16px;
      }
    }
  }
}
</style>
